import { LocationMarkerIcon } from "@heroicons/react/outline";
import { get, isEmpty, isNaN, keys, mean, sum, toUpper } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import FeatureExplanation from "../../../components/FeatureExplanation";
import Hex from "../../../components/Hex";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import FilterLabels from "../../../components/labels/FilterLabels";
import LocationActionsButton from "../../../components/LocationActionsButton";
import UpgradeSimulationsModal from "../../../components/modals/UpgradeSimulationsModal";
import NotificationBar from "../../../components/NotificationBar";
import { useAuthentication } from "../../../context/authentication";
import { useBillingPlans } from "../../../context/billingPlans";
import { useFilteredList } from "../../../context/filteredList";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useLocations } from "../../../context/locations";
import { useMembers } from "../../../context/members/MembersProvider";
import { ModalConfig } from "../../../context/toast/ToastProvider";
import { useTransmitScales } from "../../../context/transmitScales";
import { useWorkspaces } from "../../../context/workspaces/WorkspacesProvider";
import classNames from "../../../utils/classNames";
import { Entity } from "../../../utils/constants";
import {
  classificationBoundaries,
  classificationColorMap,
  getClassification
} from "../../../utils/hotspotUtils";
import { HotspotLocation, PlanType } from "../../../utils/types";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import { locationsExplanationParagraphs } from "../../Workspace/LocationsData";
import LockedWorkspace from "../../Workspace/LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../../Workspace/LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../../Workspace/LockedWorkspaceByMembers";

interface Props {}

const LocationsList: React.FC<Props> = () => {
  const { wrapActionWithAuth } = useAuthentication();
  const browserLocation = useLocation();
  const { currentWorkspaceAllowedForCommunityPlan, currentWorkspace } =
    useWorkspaces();
  const history = useHistory();
  const { calculatedTransmitScales, isTransmitScaleActive } =
    useTransmitScales();
  const { labels } = useLabels();
  const { setNewPlan } = useBillingPlans();
  const { getIsReady } = useLoading();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const { filteredList, smartLabelFilters } = useFilteredList();
  const { locations, setEditLocationConfig } = useLocations();
  const { currentWorkspaceIsLocked } = useWorkspaces();
  const { membersLimitExceeded } = useMembers();
  const [upgradeSimulationsConfig, setUpgradeSimulationsConfig] =
    useState<ModalConfig>({
      open: false
    });

  const filteredLocationLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapLocations],
        labels,
        smartLabelFilters[Entity.location],
        Entity.location
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredLocations = useMemo(
    () => filteredList[Entity.location],
    [filteredList]
  );

  const detailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(browserLocation.pathname, {
    path: "/locations/:id/:section"
  });

  const toggleLocation = useCallback(
    (location: HotspotLocation) => {
      if (location.uuid === detailsPageMatch?.params?.id) {
        history.push("/locations");
      } else {
        history.push(
          `/locations/${location.uuid}/${
            detailsPageMatch?.params?.section || "super-simulations"
          }`
        );
      }
    },
    [detailsPageMatch?.params?.section, history, detailsPageMatch?.params?.id]
  );

  const getClassificationValue = useCallback(
    (id: string) => get(calculatedTransmitScales, id, 0), // TODO: extend based on classification selection
    [calculatedTransmitScales]
  );

  const groupedList = useMemo(() => {
    const classifiedLocations: {
      [classification: string]: HotspotLocation[];
    } = {
      excellent: [], // TODO: extend based on classification selection
      good: [],
      mediocre: [],
      poor: [],
      terrible: [],
      inactive: [],
      disabled: []
    };

    if (currentWorkspace?.active_plan?.allowed_locations === 0)
      return classifiedLocations;

    filteredLocations
      .slice(-(currentWorkspace?.active_plan?.allowed_locations || 0))
      .forEach((location) => {
        classifiedLocations[
          isTransmitScaleActive(location.uuid)
            ? getClassification(getClassificationValue(location.uuid))
            : "disabled"
        ].push(location);
      });

    return classifiedLocations;
  }, [
    currentWorkspace?.active_plan?.allowed_locations,
    filteredLocations,
    isTransmitScaleActive,
    getClassificationValue
  ]);

  const averageTransmitScale = useMemo(
    () =>
      mean(
        filteredLocations
          .filter(
            (location) =>
              isTransmitScaleActive(location.uuid) &&
              getClassificationValue(location.uuid) > 0
          )
          .map((location) => getClassificationValue(location.uuid))
      ),
    [isTransmitScaleActive, filteredLocations, getClassificationValue]
  );

  const renderLocation = useCallback(
    (location: HotspotLocation, rewardScaleClassification: string) => (
      <li key={location.uuid}>
        <div
          className={classNames(
            "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:ring-2 ring-inset ring-hotspotty-400 dark:ring-white",
            detailsPageMatch?.params?.id === location.uuid && "ring-2"
          )}
          onClick={() => {
            toggleLocation(location);
          }}
        >
          <div className="flex-shrink-0">
            <span className="inline-block relative">
              <LocationMarkerIcon
                className="h-6 w-6"
                style={{
                  color: (classificationColorMap as any)[
                    rewardScaleClassification
                  ]
                }}
              />
            </span>
          </div>
          <div className="flex-1 min-w-0 focus:outline-none cursor-pointer">
            <span className="absolute inset-0" aria-hidden="true" />
            <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
              {location.name}
            </p>
            <DisplayLabels linkedUuid={location.uuid} size="small" />
          </div>

          <LocationActionsButton location={location} />
        </div>
      </li>
    ),
    [detailsPageMatch?.params?.id, toggleLocation]
  );

  const isLoading = useMemo(
    () => isEmpty(locations) && !getIsReady([LoadingKey.workspaceLocations]),
    [getIsReady, locations]
  );

  const renderFilterSection = useMemo(
    () => (
      <div>
        <div className="flex-1 flex px-4 pb-2">
          <FilterLabels
            className="mt-2 w-full"
            entity={Entity.location}
            filteredLabels={filteredLocationLabels}
            smartLabels={keys(smartLabelFilters[Entity.location]).map((key) =>
              getSmartLabelFromKey(key, Entity.location)
            )}
            filterKey={LabelFilterKey.mapLocations}
          />
        </div>
      </div>
    ),
    [filteredLocationLabels, smartLabelFilters]
  );

  if (currentWorkspaceIsLocked) {
    return (
      <LockedWorkspace
        title="Payment failed"
        paragraphs={[
          "Your subscription payment failed. Please resolve the issue with your payment method."
        ]}
        buttonText="Update payment details"
        buttonLink="/workspaces/settings/billing"
      />
    );
  }

  if (!currentWorkspaceAllowedForCommunityPlan) {
    return <LockedWorkspaceByCommunityPlan />;
  }

  if (membersLimitExceeded) {
    return <LockedWorkspaceByMembers />;
  }

  if (isLoading) {
    return <CenteredLoading className="h-full" />;
  }

  if (isEmpty(locations)) {
    return (
      <FeatureExplanation
        paragraphs={locationsExplanationParagraphs.concat(
          "This section only lists locations that are within the purple zone around the center of the map. Zoom out to see that zone." as any
        )}
      >
        <button
          type="button"
          className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
          onClick={wrapActionWithAuth(() =>
            setEditLocationConfig({ show: true })
          )}
        >
          Add location
        </button>
      </FeatureExplanation>
    );
  }

  if (isEmpty(filteredLocations)) {
    return (
      <>
        {renderFilterSection}

        <div className="z-10 sticky top-0 border-b border-t border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
          <div className="text-sm font-medium">0 locations</div>
        </div>
        <div className="px-6 pt-6 pb-4">
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            No locations to show.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {renderFilterSection}

      {currentWorkspace &&
        currentWorkspace?.counts?.location >
          currentWorkspace.active_plan?.allowed_locations && (
          <NotificationBar
            linkTitle="Increase simulations limit"
            onClick={() => {
              if (currentWorkspace?.active_plan?.type !== PlanType.community) {
                setNewPlan((prevState) => ({
                  ...prevState,
                  simulationAmount: currentWorkspace?.counts.location || 0
                }));
                setUpgradeSimulationsConfig({ open: true });

                return;
              }

              history.push("/pricing");
            }}
            content={`This workspace has a limit of ${currentWorkspace?.active_plan?.allowed_locations} locations and your workspace has ${currentWorkspace?.counts?.location}.`}
          />
        )}

      <UpgradeSimulationsModal
        config={upgradeSimulationsConfig}
        setConfig={setUpgradeSimulationsConfig}
      />

      <div className="z-10 sticky top-0 border-t border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
        <div className="text-sm font-medium">
          {filteredLocations.length.toLocaleString(undefined)} location
          {filteredLocations.length !== 1 && "s"}
        </div>
        {!isNaN(averageTransmitScale) && (
          <div className="text-xs">
            Avg transmit scale:{" "}
            {averageTransmitScale.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </div>
        )}
      </div>

      <nav
        className="flex-1 min-h-0 relative overflow-y-auto"
        aria-label="Items list"
      >
        {keys(groupedList).map((rewardScaleClassification) => (
          <div key={rewardScaleClassification}>
            <div className="z-10 sticky top-0 border-t border-b border-gray-200 bg-gray-50 px-6 py-1 text-sm font-medium text-gray-500 flex items-center dark:border-gray-500 dark:bg-gray-700 dark:text-gray-300">
              <Hex
                fillColor={
                  (classificationColorMap as any)[rewardScaleClassification]
                }
                width={10.5}
                height={12}
              />
              <h3 className="ml-2">
                {toUpper(`${rewardScaleClassification} transmit scale`)}
                <span className="ml-2 text-xs font-normal">
                  {classificationBoundaries[rewardScaleClassification] &&
                    sum(classificationBoundaries[rewardScaleClassification]) >
                      0 &&
                    `${classificationBoundaries[rewardScaleClassification][0]} to ${classificationBoundaries[rewardScaleClassification][1]}`}
                </span>
              </h3>
            </div>
            <ul className="z-0 divide-y divide-gray-200 dark:divide-gray-500">
              {groupedList[rewardScaleClassification].map((location) =>
                renderLocation(location, rewardScaleClassification)
              )}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
};

export default LocationsList;
